import { Backdrop } from "@mui/material";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import LanguagesForm from "../../../../common/components/functional/languagesForm/LanguagesForm";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import {
  Language,
  LanguageSkillCode,
  LanguageSkillLevel,
  Talent,
} from "../../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentLanguages,
} from "../../../talents/talentsSlice";
import { UpdateTalentLanguages } from "../../../talents/types/commands";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

type FormValues = {
  languageCode: LanguageSkillCode | "";
  skillLevel: LanguageSkillLevel | "";
};

type Props = {
  onClose: () => void;
};

const TalentAddLanguage = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const existingLanguageCodes =
    talent?.languages?.values?.map((value) => value.languageCode) || [];

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const defaultValues: FormValues = {
    languageCode: "",
    skillLevel: "",
  };

  const methods = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { dirtyFields },
  } = methods;

  const onSubmit = (data: FormValues) => {
    setIsLoading(true);

    if (talentId && data.languageCode && data.skillLevel) {
      const currentLanguages = talent.languages || {
        values: [],
        version: 0,
      };

      const addedLanguage: Language = {
        id: nanoid(),
        languageCode: data.languageCode,
        skillLevel: data.skillLevel,
      };

      const newLanguages = currentLanguages.values
        ? [...currentLanguages.values, addedLanguage]
        : [addedLanguage];

      const commandPayload: UpdateTalentLanguages = {
        talentId: talentId,
        version: currentLanguages.version,
        languages: newLanguages,
      };

      dispatch(updateTalentLanguages(commandPayload)).then((action) => {
        setIsLoading(false);

        return isFulfilled(action) && onClose();
      });
    } else {
      setIsLoading(false);
    }
  };

  const saveDisabled =
    !dirtyFields.languageCode || !dirtyFields.skillLevel || isLoading;

  return (
    <FormProvider {...methods}>
      <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
        <DialogContentWrapper
          title={t("languageSkill:language")}
          margin
          saveDisabled={saveDisabled}
          onSave={handleSubmit(onSubmit)}
          onCancel={onClose}
        >
          <Backdrop
            open={isLoading}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              zIndex: 1000,
            }}
          >
            <LoadingSpinner />
          </Backdrop>
          <LanguagesForm disabledLanguageCodes={existingLanguageCodes} />
        </DialogContentWrapper>
      </UnsavedChangesProvider>
    </FormProvider>
  );
};

export default TalentAddLanguage;
